/**
 * La sous-traitance : qui fait quoi sur un chantier, et pour combien.
 *
 * Décidé avec le patron le 30 octobre 2026, deuxième volet du phasage tous
 * corps d'état. Un chantier confie à une autre entreprise un **lot** entier,
 * une **phase**, ou une **tâche** décrite en clair, pour un montant hors
 * taxes. Le sous-traitant n'a pas de compte : ces données sont celles du
 * bureau.
 *
 * Ses pièces obligatoires (`documents.ts`) et ses factures (`invoices.ts`)
 * ont leur module, comme l'agrément du client et la garantie de paiement
 * (`approval.ts`).
 */
import { round2 } from "../../money.js";
import type { SubcontractApprovalStatus, SubcontractPaymentGuarantee } from "./approval.js";

export interface Subcontractor {
  id: string;
  name: string;
  /** Le corps de métier, en clair : « Électricité ». */
  trade: string | null;
  siret: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  /** Ce que la déclaration de sous-traitance (DC4) demande. */
  legalForm: string | null;
  /** RCS ou répertoire des métiers : « RCS Nantes 853 322 915 ». */
  registration: string | null;
  signatoryName: string | null;
  signatoryRole: string | null;
  /** Micro, petite ou moyenne entreprise, ou artisan. Nul : pas dit. */
  isSme: boolean | null;
  bankName: string | null;
  iban: string | null;
  notes: string | null;
  archived: boolean;
}

export type SubcontractStatus = "draft" | "signed" | "completed" | "cancelled";

export const SUBCONTRACT_STATUS_LABELS: Record<SubcontractStatus, string> = {
  draft: "À signer",
  signed: "Signé",
  completed: "Terminé",
  cancelled: "Annulé",
};

/** Ce qu'un chantier confie à un sous-traitant. */
export interface ProjectSubcontract {
  id: string;
  projectId: string;
  subcontractorId: string;
  subcontractorName: string;
  /** Un lot entier, ou une phase, ou ni l'un ni l'autre et `scope` le dit. */
  lotId: string | null;
  phaseId: string | null;
  scope: string | null;
  amountHt: number;
  status: SubcontractStatus;
  signedOn: string | null;
  /** Loi de 1975, art. 3 : présenté au client, puis agréé ou refusé. */
  approvalStatus: SubcontractApprovalStatus;
  presentedOn: string | null;
  approvalDecidedOn: string | null;
  /** Loi de 1975, art. 14 : caution, délégation, ou paiement direct en marché public. */
  paymentGuarantee: SubcontractPaymentGuarantee;
  /** Marché public : la référence que l'acheteur a donnée, et le lot. */
  publicContractRef: string | null;
  /** « Prix fermes », ou la formule de révision du contrat. */
  priceRevision: string | null;
  /** Marché public : le sous-traitant demande une avance. */
  advanceRequested: boolean;
  notes: string | null;
}

/** Ce que le calcul de la marge retient d'un marché. */
export type SubcontractCostRef = Pick<ProjectSubcontract, "amountHt" | "status" | "lotId" | "phaseId"> & {
  /** Le HT que le sous-traitant a déjà facturé sur ce marché. */
  invoicedHt?: number;
};

interface PhaseProgressRef {
  id: string;
  lotId?: string | null;
  progressPercent: number;
}

export interface SubcontractCost {
  /** Le total des marchés, annulés exclus. */
  committedHt: number;
  /** La part réalisée : l'avancement de ce qui est confié, ou le facturé s'il le dépasse. */
  realisedHt: number;
}

/**
 * L'avancement de ce qu'un marché confie, de 0 à 100.
 *
 *   · **terminé** : 100, quoi qu'en disent les phases ;
 *   · **une phase** : son avancement ;
 *   · **un lot** : la moyenne de ses phases — zéro s'il n'en a pas ;
 *   · **une tâche décrite** : zéro tant que le marché n'est pas terminé. Rien
 *     ne dit où elle en est, et un coût qu'on devine ferait une marge qu'on
 *     invente.
 */
export function subcontractProgress(
  contract: SubcontractCostRef,
  phases: readonly PhaseProgressRef[],
): number {
  if (contract.status === "completed") return 100;
  if (contract.status === "cancelled") return 0;
  if (contract.phaseId) {
    return phases.find((p) => p.id === contract.phaseId)?.progressPercent ?? 0;
  }
  if (contract.lotId) {
    const duLot = phases.filter((p) => p.lotId === contract.lotId);
    if (duLot.length === 0) return 0;
    return duLot.reduce((s, p) => s + p.progressPercent, 0) / duLot.length;
  }
  return 0;
}

/**
 * Ce que la sous-traitance coûte au chantier, engagé et réalisé.
 *
 * La marge retient le **réalisé**, pas l'engagé : un marché de 50 000 €
 * signé au premier jour, comparé à un acompte de 30 %, ferait crier à la
 * perte un chantier qui va bien. Le coût suit l'avancement de ce qui est
 * confié, comme le facturé suit celui des travaux.
 *
 * Un marché « à signer » compte : quand le sous-traitant est sur place et
 * que la phase avance, le travail se paiera, signature ou non.
 *
 * **Le facturé l'emporte quand il dépasse l'avancement** : une situation de
 * 60 % sur une phase déclarée à 40 % est un coût de 60 %. Le plus grand des
 * deux, jamais la somme — la facture paie le travail que l'avancement
 * compte déjà. Un marché annulé ne compte plus que ce qu'il a facturé.
 */
export function subcontractCost(
  contracts: readonly SubcontractCostRef[],
  phases: readonly PhaseProgressRef[],
): SubcontractCost {
  let committed = 0;
  let realised = 0;
  for (const c of contracts) {
    const facture = c.invoicedHt ?? 0;
    if (c.status === "cancelled") {
      realised += facture;
      continue;
    }
    committed += c.amountHt;
    realised += Math.max((c.amountHt * subcontractProgress(c, phases)) / 100, facture);
  }
  return { committedHt: round2(committed), realisedHt: round2(realised) };
}

/**
 * Le marché qui confie une phase, directement ou par son lot. Un marché
 * annulé ne confie plus rien ; une tâche décrite ne désigne aucune phase.
 */
export function subcontractsOfPhase<T extends Pick<ProjectSubcontract, "lotId" | "phaseId" | "status">>(
  contracts: readonly T[],
  phase: { id: string; lotId?: string | null },
): T[] {
  return contracts.filter(
    (c) =>
      c.status !== "cancelled" &&
      (c.phaseId === phase.id || (c.lotId != null && c.lotId === phase.lotId)),
  );
}
